import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { http, type TenantId } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { humaniseRole } from '../lib/labels';

type InvitationSummary = { email: string; role: string; tenant_name: string | null; expires_at: string | null };
type AcceptResponse = { token: string; tenant_id: TenantId; user: { id: number; email: string } };

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const nav = useNavigate();
  const { loginWithToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InvitationSummary | null>(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('No invitation token in the URL.');
      setLoading(false);
      return;
    }
    http.get<InvitationSummary>(`/auth/invitation?token=${encodeURIComponent(token)}`)
      .then(setInvite)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await http.post<AcceptResponse>('/auth/accept-invite', {
        token, ...form,
      });
      await loginWithToken(r.token, r.tenant_id);
      nav('/', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ maxWidth: 480, margin: '80px auto' }} className="card muted">Loading invitation…</div>;

  if (error && !invite) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto' }} className="card stack">
        <h1>Invitation</h1>
        <div style={{ color: 'var(--color-danger)' }}>{error}</div>
        <p className="muted">Ask whoever invited you to send a fresh link, or <Link to="/signup">create your own chambers</Link>.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '60px auto' }}>
      <h1>You're invited to {invite?.tenant_name || 'a Litigate chambers'}</h1>
      <p className="muted">Invited as <b>{humaniseRole(invite?.role)}</b> on <b>{invite?.email}</b>. Set a password to join.</p>
      <form onSubmit={submit} className="card stack">
        <div className="row">
          <div className="grow">
            <label>First name</label>
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
          </div>
          <div className="grow">
            <label>Last name</label>
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
          </div>
        </div>
        <div>
          <label>Email</label>
          <input value={invite?.email} disabled />
        </div>
        <div>
          <label>Password</label>
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
        </div>
        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
        <button type="submit" className="primary" disabled={submitting}>
          {submitting ? 'Joining…' : 'Join ' + (invite?.tenant_name || 'chambers')}
        </button>
      </form>
    </div>
  );
}
